import { RFC } from '@/common/types/rfc.enum';
import { Personalidad } from '../types/personalidad.enum';
const RFC_REGEX =
  /^[A-ZÑ&]{3,4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[0-1])[A-Z0-9]{2}[0-9A]$/u;

/**
 * Validates an RFC for format and checks if it's a generic foreign RFC.
 * This function is intended for pre-validation before a uniqueness check.
 *
 * @param rfc The RFC string to validate.
 * @param validateGenericForeignRfc If true, checks against "XEXX010101000".
 * @returns `true` if the RFC is valid for a uniqueness check (correct format and not generic foreign), `false` otherwise.
 */
export const isValidRfc = (
  rfc: string | undefined | null,
  validateGenericRfc = true,
  validateGenericForeignRfc = true,
): boolean => {
  if (!rfc) {
    return false; // Or true if an empty/null RFC should pass this pre-check by default
  }

  if (validateGenericRfc) {
    // RFC Genérico
    if (rfc.toUpperCase() === RFC.PUBLICO_GENERAL) {
      return false; // This RFC is valid in format but not for DB uniqueness lookup
    }
  }

  if (validateGenericForeignRfc) {
    // RFC Genérico para clientes del extranjero - should not proceed to uniqueness check
    if (rfc.toUpperCase() === RFC.EXTRANJERO) {
      return false; // This RFC is valid in format but not for DB uniqueness lookup
    }
  }

  return RFC_REGEX.test(rfc);
};

/**
 * @author: Omar Sánchez Martínez
 * @since: 04/01/2025
 * @description: Validates an RFC for query enablement purposes (allows Público en General and foreign RFC)
 * @param rfc The RFC string to validate
 * @returns `true` if the RFC is valid for fetching SAT catalogs, `false` otherwise
 */
export const isValidRfcForQueries = (rfc: string | undefined | null): boolean => {
  if (!rfc) {
    return false;
  }

  const rfcUpper = rfc.toUpperCase();

  // Allow Público en General and foreign RFC for catalog queries
  if (rfcUpper === RFC.PUBLICO_GENERAL || rfcUpper === RFC.EXTRANJERO) {
    return true;
  }

  // For regular RFCs, use the standard validation
  return RFC_REGEX.test(rfc);
};

/**
 * Determines the personality type (Física or Moral) based on RFC length
 * @param rfc - The RFC to analyze
 * @returns 'Física' for individuals (13 chars), 'Moral' for companies (12 chars), or error message
 */
export const getPersonalidad = (rfc: string): string => {
  // Special case for Público en General - it's always Persona Física (13 characters)
  if (rfc === RFC.PUBLICO_GENERAL) {
    return Personalidad.FISICA;
  }

  if (rfc === RFC.EXTRANJERO) {
    return Personalidad.FISICA;
  }

  if (!isValidRfc(rfc)) {
    return 'el RFC ingresado no es válido';
  }

  // 12 characters = Persona Moral (company)
  // 13 characters = Persona Física (individual)
  return rfc.length === 12 ? Personalidad.MORAL : Personalidad.FISICA;
};
